// ===========================================
// BUSTED! BLACKJACK - SOCKET STORE
// ===========================================

import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import type { ClientMessage, ServerMessage, PlayerConnectionWarningMessage } from '@busted-blackjack/shared';
import { useAuthStore } from './authStore';
import { useGameStateStore } from './gameStateStore';
import { getClientTableMaster } from '../utils/ClientTableMaster';

// Persisted table ID for HMR recovery
let persistedTableId: string | null = null;

export function setPersistedTableId(tableId: string | null): void {
  persistedTableId = tableId;
}

export function getPersistedTableId(): string | null {
  return persistedTableId;
}

// Reconnection callback type - components can subscribe to be notified when reconnection happens
type ReconnectionCallback = () => void;

// Store reconnection callbacks
const reconnectionCallbacks: Set<ReconnectionCallback> = new Set();

// Debounce tracking to prevent multiple notifications in same reconnection cycle
let lastReconnectionNotifyTime = 0;
const RECONNECTION_DEBOUNCE_MS = 2000; // Ignore duplicate notifications within 2 seconds

// Subscribe to reconnection events (returns unsubscribe function)
export function onReconnect(callback: ReconnectionCallback): () => void {
  reconnectionCallbacks.add(callback);
  return () => reconnectionCallbacks.delete(callback);
}

// Notify all subscribers that reconnection happened
// Debounced to prevent multiple notifications from rapid reconnection events
function notifyReconnection(): void {
  const now = Date.now();
  if (now - lastReconnectionNotifyTime < RECONNECTION_DEBOUNCE_MS) {
    console.log('[SocketStore] Skipping duplicate reconnection notification (debounced)');
    return;
  }
  lastReconnectionNotifyTime = now;

  console.log('[SocketStore] Notifying reconnection to', reconnectionCallbacks.size, 'subscribers');
  reconnectionCallbacks.forEach((cb) => {
    try {
      cb();
    } catch (e) {
      console.error('[SocketStore] Reconnection callback error:', e);
    }
  });
}

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  reconnectAttempt: number;
  connectionError: string | null;
  wasKickedFromOtherLocation: boolean;
  /** Browser network status via navigator.onLine (instant detection) */
  isNetworkOffline: boolean;

  // Actions
  connect: () => void;
  disconnect: () => void;
  forceReconnect: () => void;
  sendMessage: (message: ClientMessage) => void;
  clearKickedFlag: () => void;
}

const BACKEND_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
  reconnectAttempt: 0,
  connectionError: null,
  wasKickedFromOtherLocation: false,
  isNetworkOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,

  connect: () => {
    const { socket, isConnected, isConnecting } = get();

    // Don't connect if already connected or connecting
    if (isConnected || isConnecting) {
      console.log('[SocketStore] connect() skipped: isConnected=', isConnected, 'isConnecting=', isConnecting);
      return;
    }

    // If socket exists but is disconnected, clean it up first
    if (socket && !socket.connected) {
      console.log('[SocketStore] Cleaning up stale disconnected socket');
      socket.removeAllListeners();
      socket.disconnect();
    }

    console.log('[SocketStore] Creating new socket connection to', BACKEND_URL);
    set({ socket: null, isConnecting: true, connectionError: null });

    const newSocket = io(BACKEND_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      timeout: 10000, // Connection timeout: 10 seconds
      // Auto-reconnect configuration
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
    });

    // Fallback timeout: If still "connecting" after 15 seconds, set an error
    // This catches edge cases where Socket.io doesn't emit any error
    const connectionTimeout = setTimeout(() => {
      const currentState = get();
      if (currentState.isConnecting && !currentState.isConnected) {
        console.log('[SocketStore] Connection timeout - stuck in connecting state');
        set({
          isConnecting: false,
          connectionError: 'Connection timeout',
        });
      }
    }, 15000);

    newSocket.on('connect', () => {
      console.log('[SocketStore] Socket connected, id:', newSocket.id);
      clearTimeout(connectionTimeout);

      const wasReconnecting = get().isReconnecting;
      set({
        isConnected: true,
        isConnecting: false,
        isReconnecting: false,
        reconnectAttempt: 0,
        connectionError: null,
      });

      // NOTE: Reconnection notification is handled by the 'reconnect' event handler
      // (on newSocket.io, the Manager), which fires BEFORE this 'connect' event.
      // The 'reconnect' handler sets isReconnecting: false, so wasReconnecting will
      // always be false here during reconnection. We keep this check for safety.
      if (wasReconnecting) {
        console.log('[SocketStore] This was a reconnection (fallback), notifying subscribers');
        notifyReconnection();
      } else {
        // Initial connect (not reconnection) - rejoin if we have a persisted tableId (HMR recovery)
        // Only do this for initial connect, NOT reconnection (page handlers handle that)
        const persistedId = getPersistedTableId();
        if (persistedId) {
          console.log('[SocketStore] Auto-rejoining table after initial connect (HMR):', persistedId);
          newSocket.emit('client_message', {
            type: 'join_table',
            tableId: persistedId,
            mode: 'play',
            timestamp: Date.now(),
          });
        }
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[SocketStore] Socket disconnected, reason:', reason);
      set({ isConnected: false });

      // Server forcefully disconnected us - don't auto-reconnect
      if (reason === 'io server disconnect') {
        console.log('[SocketStore] Server forced disconnect, disabling reconnection');
        // Disable Socket.io's auto-reconnect to prevent kick cycles with multiple tabs
        newSocket.io.opts.reconnection = false;
        newSocket.removeAllListeners();
        newSocket.io.removeAllListeners();
        set({ socket: null, isReconnecting: false });
      }
      // For other reasons (transport close, ping timeout, etc.), Socket.io will auto-reconnect
    });

    newSocket.on('connect_error', (error) => {
      console.log('[SocketStore] Connection error:', error.message);
      clearTimeout(connectionTimeout); // Clear the fallback timeout
      set({
        isConnecting: false,
        connectionError: error.message,
      });
    });

    // Reconnection events (on the Manager)
    newSocket.io.on('reconnect_attempt', (attempt) => {
      console.log('[SocketStore] Reconnection attempt', attempt);
      set({
        isReconnecting: true,
        reconnectAttempt: attempt,
        connectionError: null,
      });
    });

    newSocket.io.on('reconnect', (attempt) => {
      // IMPORTANT: This event fires BEFORE 'connect' on reconnection.
      // We must call notifyReconnection() HERE because this handler sets
      // isReconnecting: false, which prevents the 'connect' handler from
      // detecting it was a reconnection.
      console.log('[SocketStore] Reconnected after', attempt, 'attempts, notifying subscribers');
      set({
        isConnected: true,
        isReconnecting: false,
        reconnectAttempt: 0,
        connectionError: null,
      });
      // Notify subscribers AFTER state is updated
      notifyReconnection();
    });

    newSocket.io.on('reconnect_error', (error) => {
      console.log('[SocketStore] Reconnection error:', error.message);
      set({
        connectionError: error.message,
      });
    });

    newSocket.io.on('reconnect_failed', () => {
      console.log('[SocketStore] Reconnection failed after all attempts');
      set({
        isReconnecting: false,
        connectionError: 'Failed to reconnect after multiple attempts',
      });
    });

    // Handle server messages
    newSocket.on('server_message', (message: ServerMessage) => {
      handleServerMessage(message);
    });

    set({ socket: newSocket });
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      console.log('[SocketStore] Disconnecting socket');
      socket.removeAllListeners();
      socket.io.removeAllListeners();
      socket.disconnect();
      set({
        socket: null,
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        reconnectAttempt: 0,
      });
    }
  },

  forceReconnect: () => {
    console.log('[SocketStore] Force reconnect requested');
    const { socket } = get();

    // Tear down old socket completely
    if (socket) {
      console.log('[SocketStore] Destroying old socket');
      socket.removeAllListeners();
      socket.disconnect();
    }

    // Reset state
    set({
      socket: null,
      isConnected: false,
      isConnecting: false,
      isReconnecting: false,
      reconnectAttempt: 0,
      connectionError: null,
    });

    // Create new connection after a brief delay
    setTimeout(() => {
      console.log('[SocketStore] Creating fresh connection');
      get().connect();
    }, 100);
  },

  sendMessage: (message: ClientMessage) => {
    const { socket, isConnected } = get();
    if (socket && isConnected) {
      socket.emit('client_message', { ...message, timestamp: Date.now() });
    } else {
      console.warn('[SocketStore] Cannot send message - not connected');
    }
  },

  clearKickedFlag: () => {
    set({ wasKickedFromOtherLocation: false });
  },
}));

// ===========================================
// MESSAGE HANDLER
// ===========================================

function handleServerMessage(message: ServerMessage) {
  switch (message.type) {
    case 'connected':
      console.log('[SocketStore] Server confirmed connection');
      break;

    case 'disconnected':
      console.log('[SocketStore] Server sent disconnect message:', (message as any).reason);
      // If kicked from another location, set flag to prevent auto-reconnect and clear table ID
      if ((message as any).reason === 'Connected from another location') {
        console.log('[SocketStore] Kicked from another location, setting flag and clearing table ID');
        useSocketStore.setState({ wasKickedFromOtherLocation: true });
        setPersistedTableId(null);
      }
      break;

    case 'error':
      console.error('[SocketStore] Server error:', (message as any).code, (message as any).message);
      break;

    case 'balance_update':
      // Route through ClientTableMaster for reconciliation
      // This ensures optimistic balance updates from chip animations are properly reconciled
      // with the server's authoritative balance
      getClientTableMaster().queueServerBalance(message.balance);
      break;

    case 'table_state':
      // Handled by useGameEvents
      // Send ACK for connection health monitoring
      sendAck((message as any).table?.id);
      break;

    case 'game_events':
      // Handled by useGameEvents
      // Send ACK for connection health monitoring
      sendAck(useGameStateStore.getState().tableId);
      break;

    case 'game_event':
      // Handled by useGameEvents
      break;

    case 'player_connection_warning':
      // Forward to gameStateStore for UI handling
      handleConnectionWarning(message as PlayerConnectionWarningMessage);
      break;

    case 'chat_message':
      // TODO: Add to chat store
      break;

    default:
      // Other message types handled elsewhere
      break;
  }
}

/**
 * Send ACK message back to server for connection health monitoring.
 * This tells the server we're still receiving messages.
 */
function sendAck(tableId: string | null | undefined): void {
  if (!tableId) return;

  const { socket, isConnected } = useSocketStore.getState();
  if (socket && isConnected) {
    socket.emit('client_message', {
      type: 'ack',
      tableId,
      timestamp: Date.now(),
    });
  }
}

/**
 * Handle player connection warning from server.
 * Updates gameStateStore to show/hide WiFi animation on affected seat.
 */
function handleConnectionWarning(message: PlayerConnectionWarningMessage): void {
  const { seatPosition, userId, active } = message;
  useGameStateStore.getState().setConnectionWarning(seatPosition, userId, active);
}

// ===========================================
// NETWORK STATUS DETECTION (navigator.onLine)
// ===========================================
// Instant detection of network connectivity changes.
// This fires immediately when network is lost, unlike Socket.IO's
// ping timeout which can take 20-30 seconds.
//
// Used for tiered connection warning:
// - 0-5s: Show warning icon over player's seat
// - 5s+: Show full ReconnectingModal with blur
// ===========================================
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('[SocketStore] Network ONLINE (navigator.onLine)');
    useSocketStore.setState({ isNetworkOffline: false });
  });

  window.addEventListener('offline', () => {
    console.log('[SocketStore] Network OFFLINE (navigator.onLine)');
    useSocketStore.setState({ isNetworkOffline: true });
  });
}

// ===========================================
// DEBUG HELPER
// ===========================================
// Access via browser console: debugSocket.disconnect(), debugSocket.reconnect(), debugSocket.status()
if (typeof window !== 'undefined') {
  (window as any).debugSocket = {
    disconnect: () => {
      const state = useSocketStore.getState();
      console.log('[debugSocket] Disconnecting socket...');
      state.socket?.disconnect();
    },
    reconnect: () => {
      console.log('[debugSocket] Force reconnecting...');
      useSocketStore.getState().forceReconnect();
    },
    status: () => {
      const state = useSocketStore.getState();
      console.log('[debugSocket] Status:', {
        isConnected: state.isConnected,
        isConnecting: state.isConnecting,
        isReconnecting: state.isReconnecting,
        isNetworkOffline: state.isNetworkOffline,
        socketId: state.socket?.id,
      });
    },
  };
}
